import { isDesktopVersionLowerOrEqual } from '../services/desktopHelpers';
import { tauriInvoke } from '../services/desktopIntegration';

const CC_FALLBACK_VERSION = '10.0.0';

export interface SecureFetchOptions {
  url: string;
  method?: string;
  headers?: Record<string, string>;
  data?: any;
}

export interface MimickedResponse {
  status: number;
  statusText: string;
  headers: Record<string, string>;
  body: string;
  json: () => Promise<any>;
  text: () => Promise<string>;
  ok: boolean;
}

/**
 * Creates a MimickedResponse from extracted response data
 */
function createMimickedResponse(
  status: number,
  statusText: string,
  headers: Record<string, string>,
  body: string
): MimickedResponse {
  return {
    status,
    statusText,
    headers,
    body,
    json: async () => (body ? JSON.parse(body) : null),
    text: async () => body,
    ok: status >= 200 && status < 300,
  };
}

/**
 * Converts Tauri secure_fetch response to MimickedResponse
 */
function createMimickedResponseFromTauri(response: any): MimickedResponse {
  const { status, statusText = '', headers = {}, body } = response;
  return createMimickedResponse(status, statusText, headers, body);
}

/**
 * Converts standard fetch Response to MimickedResponse
 */
async function createMimickedResponseFromFetch(fetchResponse: Response): Promise<MimickedResponse> {
  const body = await fetchResponse.text();
  const headers: Record<string, string> = {};
  fetchResponse.headers.forEach((value, key) => {
    headers[key] = value;
  });

  return createMimickedResponse(fetchResponse.status, fetchResponse.statusText, headers, body);
}

/**
 * Fallback to regular fetch for old AvaCC versions without secure_fetch
 */
async function fallbackFetch(
  url: string,
  method: string,
  headers: Record<string, string>,
  body: string | null
): Promise<MimickedResponse> {
  const fetchOptions: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  };

  if (body) {
    fetchOptions.body = body;
  }

  const fetchResponse = await fetch(url, fetchOptions);
  return createMimickedResponseFromFetch(fetchResponse);
}

export async function secureFetch({
  url,
  method = 'GET',
  headers = {},
  data = null,
}: SecureFetchOptions): Promise<MimickedResponse> {
  const body = data ? JSON.stringify(data) : null;

  // Check if version is <= 2.5.8, if so use fallback fetch
  const shouldUseFallback = await isDesktopVersionLowerOrEqual(CC_FALLBACK_VERSION);

  if (shouldUseFallback) {
    console.warn('AvaCC version <= 2.5.8, using fallback fetch instead of secure_fetch');
    return await fallbackFetch(url, method, headers, body);
  }

  // Use secure_fetch for versions > 2.5.8
  try {
    const response: any = await tauriInvoke('secure_fetch', {
      params: { url, method, headers, body },
    });

    return createMimickedResponseFromTauri(response);
  } catch (error) {
    console.error('secureFetch error:', { url, method, error });
    throw error;
  }
}
